// RGR10 반품모니터링 — React Query 훅.
import { useMutation, useQuery } from '@tanstack/react-query'
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import {
  getReturnMonitoringItems,
  getReturnMonitoringStatusSummaries,
  saveReturnMonitoring,
  withdrawReturnMonitoring,
} from './apis'
import {
  ReturnMonitoringItemDto,
  ReturnMonitoringSaveReq,
  ReturnMonitoringSaveRes,
  ReturnMonitoringSearchReq,
  ReturnMonitoringStatusSummaryDto,
  ReturnMonitoringWithdrawReq,
  ReturnMonitoringWithdrawRes,
} from './types'

/** 메뉴코드는 소문자 'rgr10' — 탭을 닫을 때 캐시 정리(`MenuOnSessionWrap.remove`)가 이 문자열을 찾는다. */
export const returnMonitoringQueryKeys = {
  all: ['rgr10', 'RETURN_MONITORING'] as const,
  items: (filter: ReturnMonitoringSearchReq | null) => [...returnMonitoringQueryKeys.all, 'items', filter] as const,
  summary: (filter: Omit<ReturnMonitoringSearchReq, 'ordStatus'> | null) =>
    [...returnMonitoringQueryKeys.all, 'summary', filter] as const,
}

/**
 * 조회 — 필터가 없으면(진입 직후) 호출하지 않는다. [조회] 버튼은 `refetch` 로 재실행한다 —
 * 조건이 같아도 다시 눌렀으면 재조회한다(1.0 동작, GR05 선례).
 *
 * 조회 실패 문구는 화면(`ReturnMonitoringPage`)이 서버 메시지를 꺼내 직접 보여 준다 — 전역
 * `QueryCache.onError`(`src/utils/queryClient.ts`)까지 뜨면 알럿이 두 번 뜬다(erpInbound 선례).
 * `staleTime: Infinity` 로 창 포커스 복귀 등 자동 재조회를 막는다 — 조회는 [조회] 버튼 명시적
 * 액션에서만 나가야 하는 운영 화면이라, 자동 재조회가 `rows.setFromItems` 로 편집 중인 표 상태를
 * 덮어쓰면 사용자가 입력한 내용이 조용히 사라진다.
 */
export const useGetReturnMonitoringItems = (
  filter: ReturnMonitoringSearchReq | null,
): UseQueryResult<ReturnMonitoringItemDto[], Error> =>
  useQuery({
    queryKey: returnMonitoringQueryKeys.items(filter),
    queryFn: async () => {
      const res = await getReturnMonitoringItems(filter as ReturnMonitoringSearchReq)
      return res.data?.items ?? []
    },
    enabled: !!filter,
    staleTime: Infinity,
    meta: { noErrorMessage: true },
  })

/** 상태별 건수 — 탭 전환은 재호출하지 않는다(집계에서 ordStatus 필터가 항상 빠지므로 값이 같다). */
export const useGetReturnMonitoringStatusSummaries = (
  filter: Omit<ReturnMonitoringSearchReq, 'ordStatus'> | null,
): UseQueryResult<ReturnMonitoringStatusSummaryDto[], Error> =>
  useQuery({
    queryKey: returnMonitoringQueryKeys.summary(filter),
    queryFn: async () => {
      const res = await getReturnMonitoringStatusSummaries(filter as Omit<ReturnMonitoringSearchReq, 'ordStatus'>)
      return res.data?.items ?? []
    },
    enabled: !!filter,
    staleTime: Infinity,
    meta: { noErrorMessage: true },
  })

export const useSaveReturnMonitoring = (): UseMutationResult<
  ApiResponseV2<ReturnMonitoringSaveRes>,
  Error,
  ReturnMonitoringSaveReq
> => useMutation({ mutationFn: saveReturnMonitoring })

export const useWithdrawReturnMonitoring = (): UseMutationResult<
  ApiResponseV2<ReturnMonitoringWithdrawRes>,
  Error,
  ReturnMonitoringWithdrawReq
> => useMutation({ mutationFn: withdrawReturnMonitoring })
